/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import React, { useEffect, useState } from "react";
import { Canceler } from "axios";
import { useHistory } from "react-router";
import { omitBy } from "lodash-es";
import { useTranslation } from "react-i18next";
//custom libraries
import {
  useCustomerValue,
  useMessageValue,
  useProgressValue,
} from "../context";
import { useCustomerHandler } from "./rest";
import {
  CUSTOMER_FORM_FIELD_PHONE,
  LIMIT,
  REG_EX,
  ZERO_OFFSET,
} from "../constants/common";
import { CUSTOMER } from "../constants/routes";
import { TableAction, TableDialogOption, KeyLookup } from "../constants/table";
import {
  TABLE_ACTION,
  TABLE_ACTIONS,
  LOGONID,
  FIRSTNAME,
  LASTNAME,
  STATUS,
} from "../constants/customer";
import { useShopOnBehalf } from "./use-shop-on-behalf";
import { Message, MessageType } from "../constants/message";
import {
  SORT_FIRSTNAME_ASC,
  SORT_FIRSTNAME_DESC,
  SORT_LASTNAME_ASC,
  SORT_LASTNAME_DESC,
  SORT_LOGINID_ASC,
  SORT_LOGINID_DESC,
  SORT_STATUS_ASC,
  SORT_STATUS_DESC,
} from "../constants/customer";

export const useCustomerSearchForm = () => {
  const isV9 = process.env.COMMERCE_VERSION === "9";
  const { setInProgress } = useProgressValue();
  const {
    updateCustomerRequest,
    resetPasswordRequest,
    getCustomerByIdRequest,
    searchCustomerRequest,
    searchCustomerV2Request,
    getCustomerByLogonIdRequest,
    updateCustomerV2Request,
  } = useCustomerHandler();
  const { searchResult, setSearchResult } = useCustomerValue();
  const { shopOnBehalf } = useShopOnBehalf();
  const [dialogOption, setDialogOption] = useState<TableDialogOption | any>({});
  const [sortOrder, setSortOrder] = useState<string>(() => "asc");
  const [orderBy, setOrderBy] = useState<string>(() => "");
  const [sortParam, setSortParam] = useState<any>(() => "");

  const cancels: any = { cancels: [] };

  const history = useHistory();

  const { t } = useTranslation();

  const customerParameterInit = {
    logonId: "",
    firstName: "",
    lastName: "",
    email1: "",
    [CUSTOMER_FORM_FIELD_PHONE]: "",
    address1: "",
    zipcode: "",
    country: "",
    state: "",
  };
  const [advancedSearch, setAdvancedSearch] = useState<boolean>(false);
  const [customerParameter, setCustomerParameter] = useState<any>(
    customerParameterInit
  );

  const [chips, setChips] = React.useState<any>({});
  const [formError, setFormError] = React.useState<any>({});
  const { setMessage } = useMessageValue();

  const [showForm, setShowForm] = React.useState<boolean>(true);

  const toggleAdvanceSearch = (event: any) => {
    setAdvancedSearch(!advancedSearch);
  };

  const validateInput = (name: string, value: string): boolean => {
    const newFormError = Object.assign({}, formError);
    switch (name) {
      case CUSTOMER_FORM_FIELD_PHONE: {
        if (!value || REG_EX.PHONE.test(value)) {
          delete newFormError.phone1;
        } else {
          newFormError.phone1 = true;
        }
        setFormError(newFormError);
        return !!newFormError.phone1;
      }
      default: {
        if (!value || REG_EX.ALPHA_NUMERIC_SPECIAL_CHAR.test(value)) {
          delete newFormError[name];
        } else {
          newFormError[name] = true;
        }
        setFormError(newFormError);
        return !!newFormError[name];
      }
    }
  };

  const handleChange = (event: any) => {
    const inputName = event.target.name;
    const inputValue = event.target.value;
    setCustomerParameter({ ...customerParameter, [inputName]: inputValue });
    validateInput(inputName, inputValue);
  };

  const searchCustomer = (parameter: any) => {
    setInProgress(true);
    const [res, canceler] = searchCustomerRequest({ ...parameter });
    cancels.cancels = canceler;
    res
      .then((r: any) => {
        setSearchResult({ ...r.data, params: { ...parameter, offset: "" } });
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });
  };

  const searchCustomerV2 = (parameter: any) => {
    setInProgress(true);
    const [res, canceler] = searchCustomerV2Request({ ...parameter });
    cancels.cancels = canceler;
    let data: { userDataBeans: any[] } = { userDataBeans: [] };
    res
      .then((r: any) => {
        data = r.data;
        const cusinfoPromises = data.userDataBeans.map((userDataBean: any) => {
          const [cusinfoRes, cusinfoCanceler] = getCustomerByLogonIdRequest(
            userDataBean.userRegistry.logonId
          );
          cancels.cancels = [...cancels.cancels, ...cusinfoCanceler];
          return cusinfoRes;
        });
        return Promise.all(cusinfoPromises);
      })
      .then((responses: any) => {
        for (let i = 0; i < data.userDataBeans.length; i++) {
          data.userDataBeans[i].customerDetails =
            responses[i].data.resultList[0];
        }
        setSearchResult({ ...data, params: { ...parameter, offset: "" } });
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });
  };

  const clickSearch = (event: any) => {
    event.preventDefault();
    isV9
      ? searchCustomer({ ...customerParameter })
      : searchCustomerV2({ ...customerParameter });
  };

  const clickHeaderSearchSort = () => {
    let parameters = {};
    if (typeof sortParam === "string")
      parameters = { ...customerParameter, sort: sortParam };
    else parameters = { ...customerParameter, ...sortParam };
    searchCustomer(parameters);
  };

  const sortAction = (props: string) => {
    if (orderBy === props && sortOrder === "asc") {
      setSortOrder("desc");
    } else if (orderBy === props && sortOrder === "desc") {
      setSortOrder("asc");
    } else if (orderBy !== props && sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortOrder("asc");
    }
    setOrderBy(props);
  };

  const fetchCustomer = (id: string) => {
    const [res, canceler] = getCustomerByIdRequest(id);
    cancels.cancels = canceler;
    return res.then((r) => r.data).catch((e) => console.log(e));
  };

  const fetchCustomerAndUpdateSearchResult = (id: string) => {
    fetchCustomer(id).then((customer: any) => {
      if (searchResult.items.length > 0) {
        const items = searchResult.items.map((c: any) => {
          if (c.id === customer.id) {
            return customer;
          } else {
            return c;
          }
        });
        setSearchResult(Object.assign({ ...searchResult }, { items }));
      }
    });
  };

  const changeCustomerStatus = (customer: any) => {
    const body = isV9
      ? { status: customer?.status === 1 ? 0 : 1 }
      : { userStatus: customer?.userRegistry?.status === "1" ? "0" : "1" };
    const [res, canceler] = isV9
      ? updateCustomerRequest(body, customer.id)
      : updateCustomerV2Request(body, customer.userId);
    cancels.cancels = canceler;
    res
      .then((r: any) =>
        isV9
          ? fetchCustomerAndUpdateSearchResult(customer.id)
          : searchCustomerV2({ ...customerParameter })
      )
      .catch((e) => console.log(e));
  };

  const clickResetPassword = (customer: any) => {
    const [res, canceler] = resetPasswordRequest(customer.id);
    cancels.cancels = canceler;
    const { logonId } = customer;
    res
      .then((r: any) => {
        const message: Message = {
          message: t("Success.Resetpassword", { logonId: logonId }),
          type: MessageType.SUCCESS,
        };
        setMessage(message);
      })
      .catch((e) => console.log(e));
  };

  const addSortParameter = () => {
    let parameter = {};
    if (sortParam) {
      if (typeof sortParam === "string")
        parameter = { ...customerParameter, sort: sortParam };
      else parameter = { ...customerParameter, ...sortParam };
    } else {
      parameter = { ...customerParameter };
    }
    return parameter;
  };

  const clickPrevious = (event: any) => {
    event.preventDefault();
    if (searchResult.offset > ZERO_OFFSET) {
      const parameter = addSortParameter();
      searchCustomer({
        ...parameter,
        offset: searchResult.offset - LIMIT,
      });
    }
  };

  const clickNext = (event: any) => {
    event.preventDefault();
    if (searchResult.offset + LIMIT < searchResult.count) {
      const parameter = addSortParameter();
      searchCustomer({
        ...parameter,
        offset: searchResult.offset + LIMIT,
      });
    }
  };

  const clickCustomerDetails = (customer: any) => {
    const location = {
      pathname: `${CUSTOMER}/${isV9 ? customer.id : customer.userId}`,
      state: {
        from: CUSTOMER,
        label: "Common.BackToCustomerSearchResults",
        customer,
      },
    };
    history.push(location);
  };

  const removeChip = (name: string) => {
    const newParams = { ...customerParameter, [name]: "" };
    searchCustomer(newParams);
  };

  const getActions = (customer: any): TableAction[] => {
    const actions: TableAction[] = [];
    for (const action of TABLE_ACTIONS) {
      switch (action) {
        case TABLE_ACTION.ACCESS_ACCOUNT: {
          if (customer.userRegistry.status === 1) {
            const ac: TableAction = {
              key: action,
              label: () => "Table.Actions.ShopAsUser",
              action: () => {
                shopOnBehalf(customer);
              },
            };
            actions.push(ac);
          }
          break;
        }
        case TABLE_ACTION.ENABLE_DISABLE_ACCOUNT: {
          const ac: TableAction = {
            key: action,
            label: () => {
              return (
                "Table.Actions.ToggleAccountLock" + customer.userRegistry.status
              );
            },
            action: () => {
              const dOption: TableDialogOption = {
                message: {
                  key:
                    "Customer.DisableEnableDialogHeading" +
                    customer.userRegistry.status,
                  params: {
                    user: customer.userRegistry.logonId,
                  },
                },
                onConfirm: () => {
                  changeCustomerStatus(customer);
                  setDialogOption({});
                },
                onCancel: () => setDialogOption({}),
                confirmLabel:
                  "Table.Actions.ToggleAccountLock" +
                  customer.userRegistry.status,
                cancelLabel: "Table.Actions.Cancel",
              };
              setDialogOption(dOption);
            },
          };
          actions.push(ac);
          break;
        }
        case TABLE_ACTION.REST_PASSWORD: {
          if (customer.userRegistry.status === 1) {
            const ac: TableAction = {
              key: action,
              label: () => "Table.Actions.ResetPassword",
              action: () => {
                const dOption: TableDialogOption = {
                  message: {
                    key: "Customer.ResetPasswordDialogHeading",
                    params: {
                      user: customer.logonId,
                    },
                  },
                  onConfirm: () => {
                    clickResetPassword(customer);
                    setDialogOption({});
                  },
                  onCancel: () => setDialogOption({}),
                  confirmLabel: "Table.Actions.ResetPassword",
                  cancelLabel: "Table.Actions.Cancel",
                };
                setDialogOption(dOption);
              },
            };
            actions.push(ac);
          }
          break;
        }
        case TABLE_ACTION.VIEW_DETAIL: {
          const ac: TableAction = {
            key: action,
            label: () => "Table.Actions.ViewCustomerDetails",
            action: () => {
              const location = {
                pathname: CUSTOMER + "/" + customer.userId,
                state: {
                  from: CUSTOMER,
                  label: "Common.BackToCustomerSearchResults",
                  customer,
                },
              };
              history.push(location);
            },
          };
          actions.push(ac);
          break;
        }
        default:
          throw new Error(`Table action is not supported: ${action}`);
      }
    }
    return actions;
  };

  useEffect(() => {
    const keys = Object.keys(
      omitBy(searchResult.params, (s) => String(s).trim() === "")
    );
    searchResult.count && keys.length && setShowForm(false);
    return () => {
      cancels.cancels.forEach((cancel: Canceler) => {
        return cancel();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !searchResult.params["sort"] &&
      !searchResult.params["orderByTableName"] &&
      !searchResult.params["orderByFieldName"]
    ) {
      setCustomerParameter(searchResult.params);
      const chips = omitBy(searchResult.params, (s) => String(s).trim() === "");
      setChips(chips);
      setOrderBy("");
      setSortParam("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult]);

  useEffect(() => {
    if (orderBy) {
      switch (orderBy) {
        case "logonId":
        case LOGONID:
          setSortParam(
            sortOrder === "asc" ? SORT_LOGINID_ASC : SORT_LOGINID_DESC
          );
          break;

        case "firstName":
        case FIRSTNAME:
          setSortParam(
            sortOrder === "asc" ? SORT_FIRSTNAME_ASC : SORT_FIRSTNAME_DESC
          );
          break;

        case "lastName":
        case LASTNAME:
          setSortParam(
            sortOrder === "asc" ? SORT_LASTNAME_ASC : SORT_LASTNAME_DESC
          );
          break;

        case "userRegistry.status":
        case STATUS:
          setSortParam(
            sortOrder === "asc" ? SORT_STATUS_ASC : SORT_STATUS_DESC
          );
          break;
        default:
          setSortParam("");
      }
    }
  }, [orderBy, sortOrder]);

  useEffect(() => {
    if (sortParam) {
      clickHeaderSearchSort();
    }
  }, [sortParam]);

  return {
    handleChange,
    toggleAdvanceSearch,
    advancedSearch,
    clickPrevious,
    clickNext,
    clickSearch,
    changeCustomerStatus,
    clickCustomerDetails,
    clickResetPassword,
    chips,
    removeChip,
    searchResult,
    getActions,
    sortAction,
    sortOrder,
    orderBy,
    dialogOption,
    t,
    formError,
    customerParameter,
    showForm,
    setShowForm,
    searchCustomerV2,
  };
};
